import React from "react";
import { CheckCircle2, Clock, XCircle, AlertTriangle, FileWarning, ArrowLeft, ArrowRight, Send, Save } from "lucide-react";

export function TextInput({ label, value, onChange, placeholder, type = "text", required, testid, half }) {
  return (
    <div className={half ? "flex-1 min-w-0" : ""}>
      <label className="text-xs font-semibold text-ink-600">{label}{required && <span className="text-coral"> *</span>}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 bg-surface rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 ring-coral" data-testid={testid} />
    </div>
  );
}

export function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-ink-600">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 bg-surface rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 ring-coral" data-testid={testid}>
        <option value="">Select</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export function Stepper({ steps, index }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-1 shrink-0">
          <div className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${i < index ? "bg-emerald-500 text-white" : i === index ? "bg-coral text-white" : "bg-white border border-border text-ink-600"}`}>{i < index ? "✓" : i + 1}</div>
          {i < steps.length - 1 && <div className={`w-4 h-px ${i < index ? "bg-emerald-500" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

export function WizardNav({ index, total, onBack, onNext, onSaveDraft, onSubmit, submitting }) {
  const last = index === total - 1;
  return (
    <div className="fixed bottom-0 left-0 right-0 lg:left-[45%] bg-white border-t border-border z-30">
      <div className="max-w-[600px] mx-auto p-3 flex gap-2">
        <button onClick={onBack} className="px-4 py-3 rounded-xl border border-border font-semibold text-sm flex items-center gap-1" data-testid="wizard-back"><ArrowLeft size={16} /> Back</button>
        {onSaveDraft && <button onClick={onSaveDraft} className="px-3 py-3 rounded-xl border border-border font-semibold text-sm flex items-center gap-1" data-testid="wizard-save-draft"><Save size={16} /> Draft</button>}
        {last ? (
          <button onClick={onSubmit} disabled={submitting} className="flex-1 bg-coral text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="wizard-submit"><Send size={16} /> Submit for Verification</button>
        ) : (
          <button onClick={onNext} className="flex-1 bg-coral text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="wizard-next">Next <ArrowRight size={16} /></button>
        )}
      </div>
    </div>
  );
}

const STATUS = {
  submitted: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", title: "Under Review", msg: "Your registration has been submitted and is under verification." },
  under_review: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", title: "Under Review", msg: "Your registration is being reviewed by our verification team." },
  approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", title: "Approved & Active", msg: "Congratulations! Your account is verified and active." },
  rejected: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", title: "Application Rejected", msg: "Unfortunately your application was not approved." },
  action_required: { icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50", title: "Action Required", msg: "Please correct the details below and resubmit." },
  additional_document: { icon: FileWarning, color: "text-orange-600", bg: "bg-orange-50", title: "Document Requested", msg: "We need an additional document to continue." },
  suspended: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", title: "Account Suspended", msg: "Your account has been suspended." },
};

export function StatusView({ status, reason, onResubmit, onGoDashboard }) {
  const s = STATUS[status] || STATUS.under_review;
  const canResubmit = ["rejected", "action_required", "additional_document"].includes(status);
  return (
    <div className="bg-white border border-border rounded-2xl p-6 text-center" data-testid="onboarding-status">
      <div className={`w-16 h-16 rounded-full ${s.bg} flex items-center justify-center mx-auto`}><s.icon className={s.color} size={30} /></div>
      <h2 className="font-display font-bold text-xl text-ink mt-3" data-testid="onboarding-status-title">{s.title}</h2>
      <p className="text-sm text-ink-600 mt-1">{s.msg}</p>
      {reason && <div className="mt-3 text-left bg-surface rounded-xl p-3 text-sm text-ink"><b>Reason from BiteGoFood:</b> {reason}</div>}
      {status === "approved" && <button onClick={onGoDashboard} className="mt-4 w-full bg-coral text-white font-bold py-3 rounded-xl" data-testid="go-dashboard-btn">Go to Dashboard</button>}
      {canResubmit && <button onClick={onResubmit} className="mt-4 w-full bg-coral text-white font-bold py-3 rounded-xl" data-testid="resubmit-btn">Edit &amp; Resubmit</button>}
    </div>
  );
}
